import { CoreApplicationFeeAnnounce, CorePaymentInput, CoreSelect, MMButton, SuccessPane } from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import { useTkbFeeDiscount } from '@hooks'
import { ExchangeFeeFromTkbToggle } from '@views/banking/Exchange/Elements'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import IntraAccountTransferPreview from '@withdrawals/views/IntraAccountTransferPreview'
import {
  isOwnerOrAdministrator,
  responseDoesNotContainsChallenge,
  selectCurrentUserRole,
  useAppSelector,
  useIntraAccountTransferInitiateMutation,
  useIntraAccountTransferQry,
  useShouldPayApplicationFeeQry
} from 'mmfintech-backend-api'
import type { PaymentResponse, PaymentTransferRequest } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

const IntraAccountTransfer = () => {
  const { currentStep, setCurrentStep } = useHeaderContext()

  const customerRole = useAppSelector(selectCurrentUserRole)

  // Shared TKB ("Kingdom Coin") fee-discount toggle state — same persisted
  // setting drives Exchange, Intra-account transfer and the dashboard.
  const {
    eligible: tkbDiscountEligible,
    active: payFeeFromTkb,
    onToggle: handlePayFeeFromTkbChange,
    disabled: tkbDiscountDisabled
  } = useTkbFeeDiscount()

  const history = useHistory()
  const shouldPayApplicationFee = useShouldPayApplicationFeeQry()

  const [tkbInitiateResponse, setTkbInitiateResponse] = useState<PaymentResponse | undefined>(undefined)

  const handlePreviewSuccess = useCallback(() => setCurrentStep(2), [setCurrentStep])

  const {
    accountsError,
    accountsFetching,
    sourceAccounts,
    targetAccounts,

    sourceAccount,
    setSourceAccount,
    targetAccount,
    setTargetAccount,

    amountFrom,
    amountFromError,
    setAmountFrom,
    isDataValid,
    exchangeNotSupported,
    tradeableAssetsError,

    previewData,
    previewError,
    previewLoading,

    handlePreview
  } = useIntraAccountTransferQry({
    onPreviewSuccess: handlePreviewSuccess,
    enableExchange: true
  })

  // Single initiate path — mirrors useIntraAccountTransferQry.handleInitiate but
  // adds `payFeeFromTkb` to the payload when the toggle is ON.
  const [
    initiateRequest,
    { isLoading: customInitiateLoading, error: customInitiateError, reset: resetCustomInitiate }
  ] = useIntraAccountTransferInitiateMutation()

  const handleInitiate = useCallback(() => {
    if (!sourceAccount || !targetAccount || !isDataValid) return
    const payload: PaymentTransferRequest & { payFeeFromTkb?: boolean } = {
      sourceAccountId: sourceAccount.id ?? 0,
      destinationAccountId: targetAccount.id ?? 0,
      amount: previewData?.walletAmount ?? 0,
      quoteId: previewData?.quoteId
    }
    if (payFeeFromTkb) payload.payFeeFromTkb = true
    initiateRequest(payload)
      .unwrap()
      .then(response => {
        setTkbInitiateResponse(response)
        if (responseDoesNotContainsChallenge(response)) {
          setCurrentStep(3)
        }
      })
      .catch(() => {})
  }, [
    sourceAccount,
    targetAccount,
    isDataValid,
    previewData?.walletAmount,
    previewData?.quoteId,
    payFeeFromTkb,
    initiateRequest,
    setCurrentStep
  ])

  const handleInitiateCancel = useCallback(() => {
    resetCustomInitiate()
    setTkbInitiateResponse(undefined)
    setCurrentStep(1)
  }, [resetCustomInitiate, setCurrentStep])

  const handleBackClick = () => {
    setCurrentStep(1)
    history.goBack()
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: make the check only on sourceAccount change
  useEffect(() => {
    if (sourceAccount && targetAccount && sourceAccount.id === targetAccount.id) {
      setTargetAccount(null)
    }
  }, [sourceAccount])

  return (
    <>
      {currentStep === 1 && (
        <div className={styles['send-money-container']}>
          <div className='from-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label-container'>
                {tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.FROM', 'From')}
              </div>
            </div>
            <CorePaymentInput
              selectedOption={sourceAccount}
              setSelectedAccount={setSourceAccount}
              options={sourceAccounts}
              paymentOption={sourceAccount}
              enableMaxAmountButton
              onChange={setAmountFrom}
              value={amountFrom}
              error={amountFromError}
              isLoading={accountsFetching}
              dataTest='source-account'
            />
          </div>

          <div className='to-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.TO', 'To')}</div>
            </div>
            <CoreSelect
              type='account'
              label={tr('FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.TO_ACCOUNT', 'Account')}
              options={targetAccounts}
              onChange={(_: string, v) => setTargetAccount(v)}
              value={targetAccount}
              enableAccountCreation={isOwnerOrAdministrator(customerRole)}
            />
          </div>

          {shouldPayApplicationFee && <CoreApplicationFeeAnnounce />}

          {tkbDiscountEligible && (
            <div className='intra-account-transfer-tkb-discount-row'>
              <ExchangeFeeFromTkbToggle
                isActive={payFeeFromTkb}
                disabled={previewLoading || tkbDiscountDisabled}
                onToggle={handlePayFeeFromTkbChange}
              />
            </div>
          )}

          <div className='form-actions'>
            <ErrorDisplay
              error={[
                accountsError,
                tradeableAssetsError,
                previewError,
                exchangeNotSupported
                  ? tr(
                      'FRONTEND.WITHDRAW.INTRA_ACCOUNT_TRANSFER.EXCHANGE_NOT_SUPPORTED',
                      'The selected currencies cannot be exchanged. Please, select same currency account or different currency pair.'
                    )
                  : null
              ]}
            />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              onClick={handlePreview}
              loading={previewLoading}
              disabled={accountsFetching || !isDataValid}
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <IntraAccountTransferPreview
          sourceAccount={sourceAccount}
          targetAccount={targetAccount}
          previewData={previewData}
          initiateError={customInitiateError}
          initiateLoading={customInitiateLoading}
          onContinue={handleInitiate}
          onCancel={handleInitiateCancel}
          tkbDiscountEligible={tkbDiscountEligible}
          payFeeFromTkb={payFeeFromTkb}
        />
      )}

      {currentStep === 3 && <SuccessPane response={tkbInitiateResponse} onClick={handleBackClick} />}
    </>
  )
}

export default IntraAccountTransfer
